import React from 'react';
import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { ErrorBoundaryFallback } from '@/components';
import { usePopStateDetector } from '@/hooks/usePopStateDetector';
import { usePublicLibraryList } from '@/module/public/hooks/usePublicLibraryList';
import { useScrollRestoration } from '@/module/public/hooks/useScrollRestoration';
import { useCreateSubjectNameGetter } from '@/shared/category/useCreateSubjectNameGetter';
import { useCachedCategoryInfo } from '@/shared/category/useFetchCategoryInfo';
import { isExternalPlatformQuizAllowed, useExternalPlatform } from '@/shared/externalPlatform';
import { LibraryListItem } from '@/shared/features/quiz-library';
import { EmptyQuizList } from '@/shared/quizList/EmptyQuizList';
import { useGetQuizListItemButtons } from '@/shared/quizList/hooks';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';
import { InfiniteListView } from '@/shared/quizList/InfiniteListView';
import { CreateQuizBannerItem } from './CreateQuizBannerItem';
export const QuizList = () => {
  const {
    flatData,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = usePublicLibraryList();
  const {
    searchText
  } = useSearchQuizQuery();
  const isSearchMode = Boolean(searchText);
  const isPopState = usePopStateDetector();
  useScrollRestoration(!isLoading, isPopState);
  const getQuizButtons = useGetQuizListItemButtons('public');
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const {
    mode
  } = useExternalPlatform();
  const subjectNameGetter = useCreateSubjectNameGetter(categoryTreeData?.subjectTree);

  // 외부 플랫폼 제한은 퀴즈 템플릿에만 걸린다. 폴더는 그대로 통과시킨다.
  const visibleItems = flatData.filter(item => item.kind === 'FOLDER' || isExternalPlatformQuizAllowed(item.quiz, mode));
  const buildQuizProps = (quiz: SearchQuizsetRes) => ({
    eventLocation: 'public' as const,
    isHideAuthor: false,
    alwaysDropDownMenu: false,
    subjectName: subjectNameGetter({
      subjectGroupId: quiz.categoryInfo?.subject,
      subSubjectGroupId: quiz.categoryInfo?.subSubject
    }),
    menuItems: getQuizButtons(quiz)
  });
  return <InfiniteListView items={visibleItems} isLoading={isLoading} isError={isError} hasNextPage={hasNextPage} fetchNextPage={fetchNextPage} isFetchingNextPage={isFetchingNextPage} HeaderComponent={isSearchMode ? null : <CreateQuizBannerItem />} renderItem={item => <LibraryListItem key={item.id} item={item} quizProps={buildQuizProps} />} EmptyComponent={<EmptyQuizList type={'public'} />} ErrorComponent={<ErrorBoundaryFallback />} LoadingComponent={null} data-sentry-element="InfiniteListView" data-sentry-component="QuizList" data-sentry-source-file="QuizList.tsx" />;
};